/*
 * Copyright (c) 2024, King Living
 *
 * URL helpers for URL-scoped Contentful preview (KL-302 / TSD - Contentful).
 *
 * Preview is scoped to the URL, not the whole session: `/{site}/preview/<slug>`
 * renders drafts while `/{site}/<slug>` stays published, so staff can view both
 * at once. These helpers convert between the two and are pure string ops (no SDK
 * deps) so they work the same on the client and (if needed) the server.
 *
 * Site ids are `kl-xx` (see server `resolveSiteId`); the optional leading site
 * segment is preserved when rewriting paths.
 */

// A leading URL segment that is a site id (`kl-au`) OR a locale (`en`, `en-AU`).
// The app shows site and/or locale in the path (config `app.url`), and Contentful's
// `{locale}` preview token emits e.g. `en-AU` — so `/preview` may be prefixed by a
// site, a locale, or both (`/kl-au/en-AU/preview/...`). `kl-au` also matches the
// `xx-xx` shape, so one pattern covers site ids and locale codes alike. The
// `(?=\/|$)` lookahead anchors each segment to a WHOLE path segment so it never
// swallows the start of a real slug (e.g. `/kl-au/blog-x` must not capture `/bl`).
const PREFIX_SEG = '\\/[a-z]{2}(?:-[a-z]{2})?(?=\\/|$)';

/** Zero or more leading site/locale segments. */
const PREFIX_SRC = `(?:${PREFIX_SEG})*`;

/** Matches `/preview`, `/kl-au/preview`, `/en-AU/preview`, … (with/without a tail). */
const PREVIEW_PATH_RE = new RegExp(`^${PREFIX_SRC}\\/preview(?:\\/|$)`, 'i');

/** Captures the leading site/locale segment(s) (e.g. `/kl-au`, `/en-AU`) if present. */
const PREFIX_CAPTURE_RE = new RegExp(`^(${PREFIX_SRC})`, 'i');

/** Collapse accidental `//` runs in a pathname (e.g. from a leading-slash slug). */
const collapseSlashes = (pathname) => String(pathname || '').replace(/\/{2,}/g, '/');

/**
 * Contentful auto-generated entry ids are base62 tokens (A-Za-z0-9), typically 22
 * chars and never kebab/snake-cased. Real content slugs are kebab-case (hyphens)
 * or multi-segment, so this distinguishes a `/preview/<entryId>` URL from a
 * `/preview/<slug>` one without a network round-trip.
 */
const ENTRY_ID_RE = /^[A-Za-z0-9]{20,}$/;

/**
 * Whether a pathname is a preview path (`/{site}/preview/...`).
 *
 * @param {string} pathname - location.pathname (includes the site prefix)
 * @returns {boolean}
 */
export function isPreviewPathname(pathname) {
  return PREVIEW_PATH_RE.test(String(pathname || ''));
}

/**
 * Map a preview pathname to its published equivalent (drop the `/preview`
 * segment, keep the site prefix). Non-preview paths are returned unchanged.
 *
 * @param {string} pathname
 * @returns {string}
 */
export function toPublishedPathname(pathname) {
  const next = collapseSlashes(
    String(pathname || '').replace(new RegExp(`^(${PREFIX_SRC})\\/preview`, 'i'), '$1')
  );

  return next === '' ? '/' : next;
}

/**
 * Map a published pathname to its preview equivalent (insert `/preview` after
 * the site prefix). Preview paths are returned unchanged.
 *
 * @param {string} pathname
 * @returns {string}
 */
export function toPreviewPathname(pathname) {
  const path = String(pathname || '');

  if (isPreviewPathname(path)) {
    return path;
  }

  const match = path.match(PREFIX_CAPTURE_RE);
  const prefix = match ? match[1] : '';
  const rest = path.slice(prefix.length);
  const restClean = rest === '/' ? '' : rest;

  return collapseSlashes(`${prefix}/preview${restClean}`);
}

/**
 * Strip a leading `preview` segment from a site-stripped Contentful slug, so the
 * `DynamicPage` resolver loads the same entry as the published URL.
 *
 * @param {string} slug - slug from `getSlugFromPathname` (no leading slash)
 * @returns {string} the real content slug (e.g. 'preview/blog-x' -> 'blog-x')
 */
export function stripPreviewSlug(slug) {
  if (slug === 'preview') {
    return '';
  }

  if (typeof slug === 'string' && slug.startsWith('preview/')) {
    // Drop the `preview/` segment, then any leftover leading slash from a
    // Contentful slug value that starts with `/` (which yields `preview//slug`).
    return slug.slice('preview/'.length).replace(/^\/+/, '');
  }

  return slug;
}

/**
 * Heuristic: is a site-stripped preview slug actually a Contentful entry id
 * (e.g. `/{site}/preview/1eZZBvL55YomgzAUOt4Ule`) rather than a content slug?
 *
 * @param {string} slug - slug from `stripPreviewSlug(getSlugFromPathname(...))`
 * @returns {boolean}
 */
export function isContentfulEntryId(slug) {
  return typeof slug === 'string' && ENTRY_ID_RE.test(slug);
}
